// File: apps/web/src/pages/signup/plans.ts
// Version: 2026.4.9 | Path: apps/web/src/pages/signup/plans.ts
//
// Shared plan definitions — extracted from PlanSelectionPage.tsx so that
// other signup-flow routes (the bridge page, the post-auth form) can
// reference plan names, prices, and Stripe Price IDs without circular
// imports or duplicated constants.
//
// PlanSelectionPage.tsx imports PLANS and the types from here.

export type BillingCycle = 'monthly' | 'annual';
export type PlanId = 'free' | 'builder' | 'professional' | 'enterprise';

export interface Plan {
  id: PlanId;
  name: string;
  tagline: string;
  monthlyPrice: number;
  annualPrice: number;
  accent: string;
  glow: string;
  icon: string;
  badge?: string;
  projects: string;
  workItems: string;
  aiEstimations: string;
  exports: string;
  features: string[];
  stripePriceIdMonthly?: string;
  stripePriceIdAnnual?: string;
}

// Colors referenced in the plan definitions. Kept inline so this file has
// no dependency on any other module — safe to import from anywhere in the
// signup flow without pulling in the whole page component.
const C = {
  dim:         'rgba(255,255,255,0.42)',
  cyan:        '#22d3ee',
  cyanGlow:    'rgba(34,211,238,0.45)',
  violet:      '#a78bfa',
  violetGlow:  'rgba(167,139,250,0.45)',
  amber:       '#fbbf24',
  amberGlow:   'rgba(251,191,36,0.45)',
};

export const PLANS: Plan[] = [
  {
    id: 'free',
    name: 'Free',
    tagline: 'For solo founders kicking the tires',
    monthlyPrice: 0,
    annualPrice: 0,
    accent: C.dim,
    glow: 'rgba(255,255,255,0.1)',
    icon: '⚙️',
    projects: '1 project',
    workItems: '50 work items',
    aiEstimations: '10 / month',
    exports: 'JSON, CSV',
    features: [
      'Community benchmarks',
      'Basic dashboard',
      'Work item tracking',
      'Session logging',
    ],
  },
  {
    id: 'builder',
    name: 'Builder',
    tagline: 'For growing teams building fast',
    monthlyPrice: 29,
    annualPrice: 290,
    accent: C.cyan,
    glow: C.cyanGlow,
    icon: '🔧',
    projects: '5 projects',
    workItems: 'Unlimited',
    aiEstimations: '100 / month',
    exports: '+ PDF, XLSX',
    features: [
      'All AI providers',
      'IRS report generator',
      'Engineering velocity dashboard',
      'CPA work paper export',
      'Priority email support',
    ],
    stripePriceIdMonthly: 'price_BUILDER_MONTHLY',
    stripePriceIdAnnual:  'price_BUILDER_ANNUAL',
  },
  {
    id: 'professional',
    name: 'Professional',
    tagline: 'For serious R&D operations',
    monthlyPrice: 79,
    annualPrice: 790,
    accent: C.violet,
    glow: C.violetGlow,
    icon: '⚡',
    badge: 'MOST POPULAR',
    projects: 'Unlimited',
    workItems: 'Unlimited',
    aiEstimations: '500 / month',
    exports: '+ CPA, VC exports',
    features: [
      'Everything in Builder, plus:',
      'CPA Work Paper exports',
      'Investor Summary reports',
      'Investor Dashboard',
      'Git integration (GitHub, GitLab, Azure DevOps)',
      'Custom benchmarks',
      'API access',
    ],
    stripePriceIdMonthly: 'price_PRO_MONTHLY',
    stripePriceIdAnnual:  'price_PRO_ANNUAL',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    tagline: 'For large organizations with custom needs',
    monthlyPrice: -1,
    annualPrice: -1,
    accent: C.amber,
    glow: C.amberGlow,
    icon: '🏛️',
    projects: 'Unlimited',
    workItems: 'Unlimited',
    aiEstimations: 'Unlimited',
    exports: '+ white-label',
    features: [
      'Everything in Professional, plus:',
      'VC Data Room reports',
      'Investor dashboard share links',
      'GAAIM compliance audit chain',
      'Full audit log access',
      'Unlimited AI estimations',
      'Custom domain & SSO',
      'White-label reports',
      'Dedicated benchmarks',
      'Priority phone support',
      'Custom contract & SLA',
      'Multi-org management',
    ],
  },
];
